import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../store/store';
import { logout } from '../store/slices/authSlice';
import { checkTokenExpiration } from '../store/api/baseQuery';
import { getLoginPath } from '../shared/utils/authRedirect';

export const useTokenValidation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token, user } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    if (token) {
      const isValid = checkTokenExpiration(token, dispatch);
      if (!isValid) {
        const redirectPath = getLoginPath(user?.userType as 'student' | 'tutor' | null);
        navigate(redirectPath, { replace: true });
      }
    } else if (!token && user) {
      dispatch(logout());
      const redirectPath = getLoginPath(user?.userType as 'student' | 'tutor' | null);
      navigate(redirectPath, { replace: true });
    }
  }, [token, user, dispatch, navigate]);

  useEffect(() => {
    if (!token) return;

    const interval = setInterval(() => {
      const isValid = checkTokenExpiration(token, dispatch);
      if (!isValid) {
        const redirectPath = getLoginPath(null);
        navigate(redirectPath, { replace: true });
      }
    }, 5 * 60 * 1000); 

    return () => clearInterval(interval);
  }, [token, dispatch, navigate]);

  return {
    isAuthenticated: !!token && !!user,
    token,
    user
  };
};

export const useAuthGuard = () => {
  const { isAuthenticated } = useTokenValidation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      const redirectPath = getLoginPath(null);
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  return isAuthenticated;
};
